import { Resolution } from 'src/domains/calculators/utilities/types';

export type ContainerName = 'Canvas' | 'Effective Canvas';

export const FRAMING_MUST_BE_GREATER_THAN_ZERO_MESSAGE =
  'Must be greater than 0';

function mustBeSmallerThanMessage(containerName: ContainerName): string {
  return `Must be smaller than ${containerName}`;
}

export type CustomFramingDecisionValidation = {
  widthWarning: string | null;
  heightWarning: string | null;
  exceedsCanvas: boolean;
};

type DimensionInput = string | number | null | undefined;

/**
 * Parses a dimension input (string or number) to a positive number.
 * Returns null if empty, not a number, or <= 0.
 */
function parsePositiveDimension(value: DimensionInput): number | null {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
}

/**
 * Validates custom framing decision dimensions:
 * 1) Each dimension must be a number greater than 0.
 * 2) When both are valid and a container is set, neither may exceed the container.
 *
 * Accepts string (e.g. raw input) or number. Use for both inline input validation
 * and download validation (pass form values or input strings).
 * containerName is used for the boundary-exceeded message (Canvas vs Effective Canvas).
 */
export function getCustomFramingDecisionValidation(
  widthInput: DimensionInput,
  heightInput: DimensionInput,
  containerResolution: Resolution | null | undefined,
  containerName: ContainerName = 'Canvas'
): CustomFramingDecisionValidation {
  const parsedWidth = parsePositiveDimension(widthInput);
  const parsedHeight = parsePositiveDimension(heightInput);

  // Both empty = pristine (e.g. user just toggled custom framing on). No warnings.
  if (parsedWidth === null && parsedHeight === null) {
    return {
      widthWarning: null,
      heightWarning: null,
      exceedsCanvas: false,
    };
  }

  const widthInvalid = parsedWidth === null;
  const heightInvalid = parsedHeight === null;

  if (widthInvalid || heightInvalid) {
    return {
      widthWarning: widthInvalid
        ? FRAMING_MUST_BE_GREATER_THAN_ZERO_MESSAGE
        : null,
      heightWarning: heightInvalid
        ? FRAMING_MUST_BE_GREATER_THAN_ZERO_MESSAGE
        : null,
      exceedsCanvas: false,
    };
  }

  if (!containerResolution) {
    return {
      widthWarning: null,
      heightWarning: null,
      exceedsCanvas: false,
    };
  }

  const { width: maxWidth, height: maxHeight } = containerResolution;
  const widthExceeds = parsedWidth > maxWidth;
  const heightExceeds = parsedHeight > maxHeight;
  const boundaryMessage = mustBeSmallerThanMessage(containerName);

  return {
    widthWarning: widthExceeds ? boundaryMessage : null,
    heightWarning: heightExceeds ? boundaryMessage : null,
    exceedsCanvas: widthExceeds || heightExceeds,
  };
}
